import React, { useState } from 'react'
import { getContacts, toggleContactPin, Contact } from '../lib/api'
import { useApi } from '../hooks/useApi'
import ContextChat from '../components/ContextChat'
import PullToRefresh from '../components/PullToRefresh'
import OverflowMenu from '../components/OverflowMenu'

export default function ContactsPage() {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const { data, loading, refresh } = useApi<Contact[]>(getContacts)

  const handlePin = async (id: number) => {
    await toggleContactPin(id)
    refresh()
  }

  if (loading) return <div className="loading">Loading contacts...</div>
  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <div className="message">No contacts yet. Sync your email to discover contacts.</div>
      </div>
    )
  }

  const selectedContact = selectedId ? data.find(c => c.id === selectedId) : null

  // Group by tier
  const key = data.filter(c => c.relationship_tier === 'key')
  const regular = data.filter(c => c.relationship_tier === 'regular')
  const peripheral = data.filter(c => c.relationship_tier === 'peripheral')

  const renderGroup = (title: string, contacts: Contact[]) => {
    if (contacts.length === 0) return null
    return (
      <>
        <div className="section-header">{title} ({contacts.length})</div>
        {contacts.map((c) => (
          <div
            key={c.id}
            className={`contact-card${selectedId === c.id ? ' selected' : ''}`}
            onClick={() => setSelectedId(prev => prev === c.id ? null : c.id)}
          >
            <div className={`cadence-dot ${c.cadence_status || ''}`} />
            <div className="contact-info">
              <div className="contact-header">
                <div className="name">
                  {c.name}
                  {!c.confirmed && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                      unconfirmed
                    </span>
                  )}
                </div>
                <OverflowMenu actions={[
                  { label: c.pinned ? '⚑ Unflag' : '⚑ Flag', onClick: () => handlePin(c.id) },
                ]} />
              </div>
              <div className="detail">
                {c.company && <span>{c.company}</span>}
                {c.title && <span> · {c.title}</span>}
              </div>
              <div className="detail">
                {c.email}
                {c.days_since_contact !== null && (
                  <span> · Last contact: {c.days_since_contact}d ago</span>
                )}
                <span> · {c.interaction_count} interactions</span>
              </div>
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <div style={{ paddingBottom: 120 }}>
      <PullToRefresh onRefresh={refresh} />
      {renderGroup('Key', key)}
      {renderGroup('Regular', regular)}
      {renderGroup('Peripheral', peripheral)}

      <ContextChat
        view="contacts"
        selectedItem={selectedContact ? {
          type: 'contact',
          id: selectedContact.id,
          label: selectedContact.name,
        } : null}
      />
    </div>
  )
}
